import React, { useState } from 'react';
import { MapPin, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  InvalidResult, PrimaryResult, NearbyResult, OutsideResult,
} from './AvailabilityResultCards';

// Primary service-area zip codes (Fuquay Varina, Holly Springs, Apex, Cary)
const PRIMARY_ZIPS = new Set([
  '27526', '27540', '27502', '27523', '27539', '27511', '27513', '27518', '27519',
]);

// Nearby NC zips where service may be offered with an additional travel fee.
const NEARBY_ZIPS = new Set([
  '27513', '27560', '27529',
  '27603', '27606', '27607', '27608', '27609', '27610', '27612', '27613', '27614',
  '27615', '27616', '27617',
  '27703', '27705', '27707', '27713',
  '27592', '27562',
]);

const cleanZip = (value) => value.replace(/\D/g, '').slice(0, 5);

const classify = (zip) => {
  if (zip.length !== 5) return 'invalid';
  if (PRIMARY_ZIPS.has(zip)) return 'primary';
  if (NEARBY_ZIPS.has(zip)) return 'nearby';
  return 'outside';
};

const RESULT_VIEWS = {
  invalid: InvalidResult,
  primary: PrimaryResult,
  nearby: NearbyResult,
  outside: OutsideResult,
};

const AvailabilityChecker = () => {
  const [zip, setZip] = useState('');
  const [status, setStatus] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus(classify(zip));
  };

  const ResultView = status ? RESULT_VIEWS[status] : null;

  return (
    <section
      className="py-16 px-4 sm:px-6 lg:px-8 bg-white"
      data-testid="availability-checker-section"
    >
      <div className="max-w-3xl mx-auto">
        <div className="bg-blue-50 border-2 border-blue-100 rounded-2xl p-8 sm:p-10">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="bg-blue-100 rounded-full p-2">
              <MapPin className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center">
              Do We Serve Your Area?
            </h2>
          </div>
          <p className="text-gray-600 text-center mb-6">
            Enter your ZIP code and we&apos;ll let you know right away.
          </p>

          <form
            onSubmit={handleSubmit}
            className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          >
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="e.g. 27526"
              value={zip}
              onChange={(e) => {
                setZip(cleanZip(e.target.value));
                setStatus(null);
              }}
              aria-label="ZIP code"
              data-testid="availability-zip-input"
              className="flex-1"
            />
            <Button
              type="submit"
              className="bg-coral-500 hover:bg-coral-600 text-white"
              data-testid="availability-check-button"
            >
              <Search className="w-4 h-4 mr-2" />
              Check Availability
            </Button>
          </form>

          {ResultView && (
            <div className="mt-6 max-w-md mx-auto" data-testid="availability-result">
              <ResultView zip={zip} />
            </div>
          )}

          <p className="text-xs text-gray-500 text-center mt-6">
            Primary areas: Fuquay Varina, Holly Springs, Apex, and Cary.
          </p>
        </div>
      </div>
    </section>
  );
};

export default AvailabilityChecker;
